<template>
  <picture class="landing-hero-picture">
    <source v-if="desktopAvif" :media="artMedia" type="image/avif" :srcset="withBust(desktopAvif)" />
    <source :media="artMedia" type="image/webp" :srcset="withBust(desktopSrc)" />
    <source v-if="mobileAvif" type="image/avif" :srcset="withBust(mobileAvif)" />
    <source type="image/webp" :srcset="withBust(mobileSrc)" />
    <img
      class="landing-hero-img"
      :class="[variant, { 'is-skeleton': isSkeleton, 'is-loaded': imgLoaded }]"
      :style="[heightVars, placeholderStyle]"
      :src="withBust(mobileSrc)"
      :alt="alt"
      :width="width"
      :height="height"
      :fetchpriority="fetchPriority"
      decoding="sync"
      @load="onImgLoad"
      @error="onSourceError"
    />
  </picture>
</template>

<script setup>
import { breakpoints } from '~/config/breakpoints';

const props = defineProps({
  desktopSrc: { type: String, required: true },
  mobileSrc: { type: String, required: true },
  alt: { type: String, default: '' },
  width: { type: Number, default: null },
  height: { type: Number, default: null },
  variant: { type: String, default: 'tile', validator: (v) => ['tile', 'banner'].includes(v) },
  fetchPriority: { type: String, default: 'high' },
  mobileHeight: { type: Number, default: 0 },
  desktopHeight: { type: Number, default: 595 },
  objectPosition: { type: String, default: 'center' },
  desktopPlaceholder: { type: String, default: '' },
  mobilePlaceholder: { type: String, default: '' },
  placeholderVariant: { type: String, default: 'lqip', validator: (v) => ['lqip', 'skeleton'].includes(v) },
});

const isSkeleton = computed(() => props.placeholderVariant === 'skeleton');

const toAvif = (url) => (/\.webp(\?|$)/i.test(url || '') ? url.replace(/\.webp(\?|$)/i, '.avif$1') : null);
const desktopAvif = computed(() => toAvif(props.desktopSrc));
const mobileAvif = computed(() => toAvif(props.mobileSrc));

const artMedia = computed(() => (props.variant === 'banner' ? `(min-width: ${breakpoints.large}px)` : `(min-width: ${breakpoints.medium}px)`));

const imgLoaded = ref(false);
const onImgLoad = () => {
  imgLoaded.value = true;
};

const MAX_RETRIES = 2;
const retries = ref(0);
const cacheBust = ref('');
let retryTimer = null;

const withBust = (url) => {
  if (!url || !cacheBust.value) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${cacheBust.value}`;
};

const onSourceError = () => {
  if (retries.value >= MAX_RETRIES) return;
  retries.value += 1;
  retryTimer = setTimeout(() => {
    cacheBust.value = `retry=${retries.value}-${Date.now()}`;
  }, 300 * retries.value);
};

watch(
  () => [props.desktopSrc, props.mobileSrc],
  () => {
    if (retryTimer) clearTimeout(retryTimer);
    retries.value = 0;
    cacheBust.value = '';
    imgLoaded.value = false;
  },
);
onBeforeUnmount(() => {
  if (retryTimer) clearTimeout(retryTimer);
});

const placeholderStyle = computed(() => {
  const desktop = props.desktopPlaceholder || props.mobilePlaceholder;
  const mobile = props.mobilePlaceholder || props.desktopPlaceholder;
  const style = {
    'object-position': props.objectPosition,
    '--bg-position': props.objectPosition,
  };

  if (isSkeleton.value || (!desktop && !mobile)) return style;

  if (mobile) style['--lqip-m'] = `url("${mobile}")`;
  if (desktop) style['--lqip-d'] = `url("${desktop}")`;
  return style;
});

const toRem = (px) => `${px / 16}rem`;
const heightVars = computed(() =>
  props.variant === 'banner'
    ? {
        '--hero-mobile-h': toRem(props.mobileHeight),
        '--hero-desktop-h': toRem(props.desktopHeight),
        '--hero-aspect': `${props.width} / ${props.desktopHeight}`,
      }
    : {
        '--hero-aspect': `${props.width} / ${props.height}`,
      },
);
</script>

<style lang="scss" scoped>
.landing-hero-picture {
  display: block;
}
.landing-hero-img {
  display: block;
  width: 100%;
  object-fit: cover;
  background-size: cover;
  background-position: var(--bg-position, center);
  background-repeat: no-repeat;
  aspect-ratio: var(--hero-aspect, auto);
}
.landing-hero-img:not(.is-skeleton) {
  background-image: var(--lqip-m, none);
}
.landing-hero-img.tile:not(.is-skeleton) {
  @include breakpoint(medium up) {
    background-image: var(--lqip-d, none);
  }
}
.landing-hero-img.banner {
  height: var(--hero-mobile-h);
  @include breakpoint(large up) {
    height: var(--hero-desktop-h);
  }
}
.landing-hero-img.banner:not(.is-skeleton) {
  @include breakpoint(large up) {
    background-image: var(--lqip-d, none);
  }
}

.landing-hero-img.is-skeleton {
  background-color: rgba($skeleton-loader-color-start, 0.05);
  animation: hero-skeleton-pulse 600ms ease-in-out alternate infinite;
}

.landing-hero-img.is-skeleton.is-loaded {
  background-color: transparent;
  animation: none;
}
@keyframes hero-skeleton-pulse {
  from {
    background-color: rgba($skeleton-loader-color-start, 0.05);
  }
  to {
    background-color: rgba($skeleton-loader-color-start, 0.025);
  }
}
@media (prefers-reduced-motion: reduce) {
  .landing-hero-img.is-skeleton {
    animation: none;
  }
}
</style>
